import { ScoreFactor } from "@inrange/building-manager-api-client/models-site";

export type ScoreTooltipOrgContext =
  | "landlord"
  | "tenant"
  | "ownerOccupier"
  // Use this on header/sidebar wrappers that don't render a score - it
  // documents that the value is intentionally a no-op rather than an
  // arbitrary placeholder, and any tooltip text built with it bails out.
  | "no-score-details";

const POSITIVE_BLURBS: Record<string, string> = {
  capacity: "a large installed capacity",
  irr: "strong returns",
  curtailment: "unrestricted export access",
  export: "high export volume",
  battery: "the application of a battery",
  landlord_tenant_pair: "multiple sites with the same landlord/tenant pair",
};

const NEGATIVE_BLURBS: Record<string, string> = {
  capacity: "modest installed capacity",
  irr: "below target returns",
  onsite_demand: "low on-site demand relative to installed capacity",
  curtailment: "high export curtailment",
  export: "low export volume",
  battery: "no battery storage",
  landlord_tenant_pair: "no shared landlord/tenant portfolio",
};

// Tenants don't see InRange returns or export-side detail on their score.
// onsite_demand is shown though - even though it's triggered off the IRR,
// the framing (system sized large relative to on-site demand) reads
// naturally from the tenant's perspective.
const TENANT_HIDDEN_KEYS = new Set(["irr", "curtailment", "export"]);

const formatList = (items: string[]): string => {
  if (items.length === 0) return "";
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
};

const isHidden = (
  key: string,
  scoreOrgContext: ScoreTooltipOrgContext
): boolean => scoreOrgContext === "tenant" && TENANT_HIDDEN_KEYS.has(key);

export const buildScoreTooltipText = (
  score: number | undefined,
  factors: ScoreFactor[] | undefined,
  scoreOrgContext: ScoreTooltipOrgContext
): string | undefined => {
  if (score === undefined || scoreOrgContext === "no-score-details") {
    return undefined;
  }
  // Score persisted but no factors stamped (manual override or legacy).
  if (!factors || factors.length === 0) {
    return `This site has a score of ${score}.`;
  }
  const positives = factors
    .filter((f) => f.status === "positive" && !isHidden(f.key, scoreOrgContext))
    .map((f) => POSITIVE_BLURBS[f.key])
    .filter((blurb): blurb is string => Boolean(blurb));
  const negatives = factors
    .filter((f) => f.status === "negative" && !isHidden(f.key, scoreOrgContext))
    .map((f) => NEGATIVE_BLURBS[f.key])
    .filter((blurb): blurb is string => Boolean(blurb));

  if (score >= 4 && positives.length > 0) {
    return `This site is scored ${score} because of ${formatList(positives)}.`;
  }
  if (score <= 2 && negatives.length > 0) {
    return `This site is scored ${score} because of ${formatList(negatives)}.`;
  }
  if (positives.length > 0 && negatives.length > 0) {
    return `This site is scored ${score}, reflecting ${formatList(positives)}, but ${formatList(negatives)}.`;
  }
  if (positives.length > 0) {
    return `This site is scored ${score}, reflecting ${formatList(positives)}.`;
  }
  if (negatives.length > 0) {
    return `This site is scored ${score}, reflecting ${formatList(negatives)}.`;
  }
  return `This site has a score of ${score}.`;
};
